// Cash Voucher (General Payable by Cash) — mirrors legacy /Accounts/GeneralPayablebyCash
// (Sl-erp-ui Views/Accounts/_GeneralPayablebyCash.cshtml): same field order, cascades and validations.
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Receipt, Loader2, RotateCcw, Send, ChevronDown, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchSelfCCList,
    fetchOtherCCList,
    fetchCCBalance,
    fetchDCAList,
    fetchSDCAList,
    fetchGSTNumbers,
    saveCashVoucher,
    clearSDCAList,
    clearDCAAndBelow,
    clearDCAList,
    clearOtherCCList,
    clearSaveResult,
    resetCashVoucher,
    selectSelfCCList,
    selectOtherCCList,
    selectCCBalance,
    selectDCAList,
    selectSDCAList,
    selectGSTNumbers,
    selectSaveLoading,
    selectSelfCCLoading,
    selectOtherCCLoading,
    selectCCBalanceLoading,
    selectDCALoading,
    selectSDCALoading,
    selectGSTLoading,
} from '../../slices/accountsSlice/cashVoucherSlice';

// ─── Constants / helpers ──────────────────────────────────────────────────────
// Option text is what legacy posts as GPCash_TransactionMode; SPGeneralCashPayment compares
// against exactly 'Self Debit' / 'Debit From Other CC'.
const MODE_SELF  = 'Self Debit';
const MODE_OTHER = 'Debit From Other CC';

const today = () => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; };
const daysAgo = (n) => { const d = today(); d.setDate(d.getDate() - n); return d; };
const toYMD = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Legacy ValidateAmountQuantity: digits with at most 2 decimals
const isAmountInput = (v) => /^\d*\.?\d{0,2}$/.test(v);
const num = (v) => parseFloat(v) || 0;

const inputCls =
    'w-full h-9 px-2.5 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40 focus:border-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-700/60 disabled:cursor-not-allowed';
const selectCls = `${inputCls} appearance-none pr-8 cursor-pointer`;

const CardHeader = ({ icon: Icon, title }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <Icon className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
    </div>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

const Field = ({ label, required, className = '', children }) => (
    <div className={`min-w-0 ${className}`}>
        <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const Select = ({ value, onChange, disabled, loading, placeholder = '-Please Select-', options, getValue, getLabel }) => (
    <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled || loading} className={selectCls}>
            <option value="">{loading ? 'Loading...' : placeholder}</option>
            {options.map((o) => (
                <option key={getValue(o)} value={getValue(o)}>{getLabel(o)}</option>
            ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const AmountInput = ({ value, onChange, disabled, readOnly }) => (
    <input
        type="text"
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        readOnly={readOnly}
        disabled={disabled}
        onChange={(e) => { if (isAmountInput(e.target.value)) onChange(e.target.value); }}
        className={`${inputCls} text-right ${readOnly ? 'bg-gray-100 dark:bg-gray-700/60 font-semibold' : ''}`}
    />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CashVoucherCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'User';

    const selfCCList       = useSelector(selectSelfCCList);
    const otherCCList      = useSelector(selectOtherCCList);
    const ccBalance        = useSelector(selectCCBalance);
    const dcaList          = useSelector(selectDCAList);
    const sdcaList         = useSelector(selectSDCAList);
    const gstNumbers       = useSelector(selectGSTNumbers);
    const saveLoading      = useSelector(selectSaveLoading);
    const selfCCLoading    = useSelector(selectSelfCCLoading);
    const otherCCLoading   = useSelector(selectOtherCCLoading);
    const ccBalanceLoading = useSelector(selectCCBalanceLoading);
    const dcaLoading       = useSelector(selectDCALoading);
    const sdcaLoading      = useSelector(selectSDCALoading);
    const gstLoading       = useSelector(selectGSTLoading);

    // ── Form state (legacy order) ─────────────────────────────────────────────
    const [voucherType,   setVoucherType]   = useState('');   // '' | 'Normal' | 'GST'
    const [txnMode,       setTxnMode]       = useState('');   // '' | MODE_SELF | MODE_OTHER
    const [selfCC,        setSelfCC]        = useState('');
    const [otherCC,       setOtherCC]       = useState('');
    const [voucherDate,   setVoucherDate]   = useState(today);
    const [paidDate,      setPaidDate]      = useState(today);
    const [dca,           setDca]           = useState('');
    const [sdca,          setSdca]          = useState('');
    const [name,          setName]          = useState('');
    const [gstNo,         setGstNo]         = useState('');
    const [gstType,       setGstType]       = useState('');   // '' | 'CGSTSGST' | 'IGST'
    const [igst,          setIgst]          = useState('');
    const [cgst,          setCgst]          = useState('');
    const [sgst,          setSgst]          = useState('');
    const [remarks,       setRemarks]       = useState('');
    const [invoiceAmount, setInvoiceAmount] = useState('');

    // Inline message box like legacy's divcashgeneralinvoiceInfoMsg
    const [message, setMessage] = useState(null); // { type: 'error' | 'success', title, lines: [] }

    const isGST = voucherType === 'GST';

    // Legacy CountFinalGenAmount
    const totalAmount = (() => {
        let t = num(invoiceAmount);
        if (isGST && gstType === 'IGST')     t += num(igst);
        if (isGST && gstType === 'CGSTSGST') t += num(cgst) + num(sgst);
        return t;
    })();

    // ── Mount ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId && roleId) dispatch(fetchSelfCCList({ uid: userId, rid: roleId }));
        return () => { dispatch(resetCashVoucher()); };
    }, [dispatch, userId, roleId]);

    // ── Cascades (legacy BindCashCCCode / BindCashDcaCode / BindCashSDcaCode) ──
    // Mode or Self CC changed: reset heads; Self Debit → DCAs of Self CC,
    // Debit From Other CC → Other CC list (excluding Self CC) and DCAs come from the Other CC.
    const applyModeAndSelfCC = (mode, cc) => {
        setOtherCC(''); setDca(''); setSdca('');
        dispatch(clearDCAList());
        dispatch(clearOtherCCList());
        if (!mode || !cc) return;
        if (mode === MODE_SELF)  dispatch(fetchDCAList(cc));
        if (mode === MODE_OTHER) dispatch(fetchOtherCCList({ id: cc, type: '2' }));
    };

    const handleVoucherTypeChange = (val) => {
        // Legacy clears the whole form when the voucher type radio changes
        resetForm(val);
        setMessage(null);
        if (val === 'GST' && gstNumbers.length === 0) dispatch(fetchGSTNumbers());
    };

    const handleModeChange = (val) => {
        setTxnMode(val);
        applyModeAndSelfCC(val, selfCC);
    };

    const handleSelfCCChange = (val) => {
        setSelfCC(val);
        dispatch(clearDCAAndBelow()); // also clears the old balance
        if (val) dispatch(fetchCCBalance(val));
        applyModeAndSelfCC(txnMode, val);
    };

    const handleOtherCCChange = (val) => {
        setOtherCC(val); setDca(''); setSdca('');
        dispatch(clearDCAList());
        if (val) dispatch(fetchDCAList(val));
    };

    const handleDCAChange = (val) => {
        setDca(val); setSdca('');
        dispatch(clearSDCAList());
        if (val) dispatch(fetchSDCAList(val));
    };

    const handleVoucherDateChange = (d) => {
        setVoucherDate(d);
        // Legacy: paid date's minDate = voucher date
        if (d && paidDate && new Date(paidDate) < new Date(d)) setPaidDate(null);
    };

    const handleGstTypeChange = (val) => {
        // Legacy GETGSTType clears all amounts
        setGstType(val);
        setIgst(''); setCgst(''); setSgst(''); setInvoiceAmount('');
    };

    const resetForm = (type = '') => {
        setVoucherType(type);
        setTxnMode(''); setSelfCC(''); setOtherCC('');
        setVoucherDate(today()); setPaidDate(today());
        setDca(''); setSdca(''); setName('');
        setGstNo(''); setGstType(''); setIgst(''); setCgst(''); setSgst('');
        setRemarks(''); setInvoiceAmount('');
        dispatch(clearDCAAndBelow());
        dispatch(clearOtherCCList());
    };

    const handleReset = () => { resetForm(''); setMessage(null); };

    // ── Submit (legacy btngeneralinvocicecash validations, same order/wording) ─
    const handleSubmit = async () => {
        const errs = [];
        if (!txnMode) errs.push('Select Mode of Transaction');
        if (!selfCC)  errs.push('Select Self Cost Center Code');
        if (txnMode === MODE_OTHER && !otherCC) errs.push('Select Other Cost Center Code');
        if (!voucherDate) errs.push('Select Date');
        if (!paidDate)    errs.push('Select Paid Date');
        if (!dca)  errs.push('Select Account Head');
        if (!sdca) errs.push('Select Sub-Account Head');
        if (!name.trim()) errs.push('Enter Name');
        if (!isGST && num(invoiceAmount) <= 0) errs.push('Enter Invoice Amount');
        if (isGST) {
            if (!gstNo)   errs.push('Select GST No');
            if (!gstType) errs.push('Select GST Type');
            if (gstType === 'IGST' && num(igst) <= 0) errs.push('Enter IGST Amount');
            if (gstType === 'CGSTSGST') {
                if (num(cgst) <= 0) errs.push('Enter CGST Amount');
                if (num(sgst) <= 0) errs.push('Enter SGST Amount');
                if (num(cgst) !== num(sgst)) errs.push('Invalid CGST SGST Amount');
            }
            if (num(invoiceAmount) <= 0) errs.push('Enter Invoice Amount');
        }
        if (!remarks.trim()) errs.push('Enter Remarks');
        if (totalAmount <= 0) errs.push('Invalid Total Amount');

        if (errs.length) {
            setMessage({ type: 'error', title: 'Please enter all required fields!', lines: errs });
            return;
        }
        setMessage(null);

        try {
            const res = await dispatch(saveCashVoucher({
                transactionMode: txnMode,
                selfCCCode:      selfCC,
                otherCCCode:     txnMode === MODE_OTHER ? otherCC : '',
                voucherDate:     toYMD(voucherDate),
                paidDate:        toYMD(paidDate),
                dcaCode:         dca,
                sdcaCode:        sdca,
                name:            name.trim(),
                remarks:         remarks.trim(),
                invoiceAmount:   num(invoiceAmount),
                totalAmount:     Number(totalAmount.toFixed(2)),
                paymentType:     isGST ? 'GST' : 'Normal',
                companyGST:      isGST ? gstNo : null,
                igstAmount:      isGST && gstType === 'IGST'     ? num(igst) : null,
                cgstAmount:      isGST && gstType === 'CGSTSGST' ? num(cgst) : null,
                sgstAmount:      isGST && gstType === 'CGSTSGST' ? num(sgst) : null,
                roleId,
                createdBy:       userName,
            })).unwrap();

            // SP returns "Approved SuccessFully,<RefNo>" on success, otherwise an error text
            const raw   = typeof res?.Data === 'string' ? res.Data : '';
            const parts = raw.split(',');
            dispatch(clearSaveResult());
            if (parts[0].trim().toLowerCase() === 'approved successfully') {
                const refNo = (parts[1] || '').trim();
                const msg = `Submitted Successfully. Reference No:- ${refNo}`;
                toast.success(msg);
                resetForm('');
                setMessage({ type: 'success', title: msg, lines: [] });
            } else {
                setMessage({ type: 'error', title: raw || 'Error Occurred While Adding General Payment Details.', lines: [] });
            }
        } catch (err) {
            dispatch(clearSaveResult());
            setMessage({
                type: 'error',
                title: (typeof err === 'string' && err) || 'Error Occurred While Adding General Payment Details.',
                lines: [],
            });
        }
    };

    const busy = saveLoading;

    // Display labels for the summary panel
    const selfCCLabel  = selfCCList.find((o) => String(o.CC_Id) === String(selfCC))?.CC_Code || '';
    const otherCCLabel = otherCCList.find((o) => String(o.CC_Id) === String(otherCC))?.CC_Code || '';
    const dcaLabel     = dcaList.find((o) => String(o.CashDCA_Id) === String(dca))?.CashDCA_Code || '';
    const sdcaLabel    = sdcaList.find((o) => String(o.CashSDCA_Id) === String(sdca))?.CashSDCA_Code || '';
    const gstAmount    = isGST ? (gstType === 'IGST' ? num(igst) : gstType === 'CGSTSGST' ? num(cgst) + num(sgst) : 0) : 0;
    const exceedsBalance = txnMode === MODE_SELF && selfCC && ccBalance != null && totalAmount > 0 && totalAmount > num(ccBalance);

    // ──────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Cash Voucher'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · General Payable by Cash</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Receipt} title="Voucher Details" />
                    <div className="p-4 space-y-4">

                        {/* Payment Type */}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                Payment Type<span className="text-rose-500 ml-0.5">*</span>
                            </span>
                            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                                {[{ v: 'Normal', l: 'Normal Voucher' }, { v: 'GST', l: 'GST Voucher' }].map(({ v, l }) => (
                                    <button
                                        key={v}
                                        type="button"
                                        onClick={() => handleVoucherTypeChange(v)}
                                        disabled={busy}
                                        className={`px-4 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 ${voucherType === v
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                    >
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {!voucherType ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6 border-t border-gray-100 dark:border-gray-700">
                                Select a payment type to start the voucher.
                            </p>
                        ) : (
                            <>
                                {/* Cost centre, dates and heads */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <Field label="Mode of Transaction" required>
                                        <Select
                                            value={txnMode}
                                            onChange={handleModeChange}
                                            disabled={busy}
                                            options={[MODE_SELF, MODE_OTHER]}
                                            getValue={(o) => o}
                                            getLabel={(o) => o}
                                        />
                                    </Field>
                                    <Field label="Self Cost Center" required>
                                        <Select
                                            value={selfCC}
                                            onChange={handleSelfCCChange}
                                            disabled={busy}
                                            loading={selfCCLoading}
                                            options={selfCCList}
                                            getValue={(o) => o.CC_Id}
                                            getLabel={(o) => o.CC_Code}
                                        />
                                        {selfCC && (
                                            <p className="mt-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                                                Balance:{' '}
                                                {ccBalanceLoading
                                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                                    : `₹${fmt(ccBalance ?? 0)}`}
                                            </p>
                                        )}
                                    </Field>
                                    {txnMode === MODE_OTHER && (
                                        <Field label="Other Cost Center" required>
                                            <Select
                                                value={otherCC}
                                                onChange={handleOtherCCChange}
                                                disabled={busy || !selfCC}
                                                loading={otherCCLoading}
                                                placeholder={selfCC ? '-Please Select-' : 'Select Self CC first'}
                                                options={otherCCList}
                                                getValue={(o) => o.CC_Id}
                                                getLabel={(o) => o.CC_Code}
                                            />
                                        </Field>
                                    )}
                                    <Field label="Date" required>
                                        <CustomDatePicker
                                            value={voucherDate}
                                            onChange={handleVoucherDateChange}
                                            minDate={daysAgo(365)}
                                            maxDate={today()}
                                            size="sm"
                                            format="DD/MM/YYYY"
                                            placeholder="Select date"
                                            disabled={busy}
                                        />
                                    </Field>
                                    <Field label="Paid Date" required>
                                        <CustomDatePicker
                                            value={paidDate}
                                            onChange={setPaidDate}
                                            minDate={voucherDate || null}
                                            maxDate={today()}
                                            size="sm"
                                            format="DD/MM/YYYY"
                                            placeholder="Select paid date"
                                            disabled={busy}
                                        />
                                    </Field>
                                    <Field label="Account Head" required>
                                        <Select
                                            value={dca}
                                            onChange={handleDCAChange}
                                            disabled={busy}
                                            loading={dcaLoading}
                                            options={dcaList}
                                            getValue={(o) => o.CashDCA_Id}
                                            getLabel={(o) => o.CashDCA_Code}
                                        />
                                    </Field>
                                    <Field label="Sub Account Head" required>
                                        <Select
                                            value={sdca}
                                            onChange={setSdca}
                                            disabled={busy || !dca}
                                            loading={sdcaLoading}
                                            options={sdcaList}
                                            getValue={(o) => o.CashSDCA_Id}
                                            getLabel={(o) => o.CashSDCA_Code}
                                        />
                                    </Field>
                                    <Field label="Name" required className="sm:col-span-2 lg:col-span-2">
                                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} className={inputCls} />
                                    </Field>
                                </div>

                                {/* GST (GST voucher only) */}
                                {isGST && (
                                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                                            <ShieldCheck className="h-3.5 w-3.5 text-indigo-500" /> GST Details
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                            <Field label="Company GST" required>
                                                <Select
                                                    value={gstNo}
                                                    onChange={setGstNo}
                                                    disabled={busy}
                                                    loading={gstLoading}
                                                    options={gstNumbers}
                                                    getValue={(o) => o.GSTNo}
                                                    getLabel={(o) => o.TaxNoName || o.GSTNo}
                                                />
                                            </Field>
                                            <Field label="GST Type" required>
                                                <Select
                                                    value={gstType}
                                                    onChange={handleGstTypeChange}
                                                    disabled={busy}
                                                    options={[{ v: 'CGSTSGST', l: 'CGST and SGST' }, { v: 'IGST', l: 'IGST' }]}
                                                    getValue={(o) => o.v}
                                                    getLabel={(o) => o.l}
                                                />
                                            </Field>
                                            {gstType === 'IGST' && (
                                                <Field label="IGST Amount" required>
                                                    <AmountInput value={igst} onChange={setIgst} disabled={busy} />
                                                </Field>
                                            )}
                                            {gstType === 'CGSTSGST' && (
                                                <>
                                                    <Field label="CGST Amount" required>
                                                        <AmountInput value={cgst} onChange={setCgst} disabled={busy} />
                                                    </Field>
                                                    <Field label="SGST Amount" required>
                                                        <AmountInput value={sgst} onChange={setSgst} disabled={busy} />
                                                    </Field>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Remarks + amount */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <Field label="Remarks" required className="sm:col-span-2">
                                        <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} className={inputCls} />
                                    </Field>
                                    <Field label="Invoice Amount" required>
                                        <AmountInput value={invoiceAmount} onChange={setInvoiceAmount} disabled={busy} />
                                    </Field>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle2} title="Voucher Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Payment Type" value={voucherType ? `${voucherType} Voucher` : ''} />
                                <SumRow label="Mode"         value={txnMode} />
                                <SumRow label="Self CC"      value={selfCCLabel} />
                                <SumRow label="CC Balance"   value={selfCC ? (ccBalanceLoading ? 'Loading…' : `₹${fmt(ccBalance ?? 0)}`) : ''} />
                                {txnMode === MODE_OTHER && <SumRow label="Other CC" value={otherCCLabel} />}
                                <SumRow label="Date / Paid"  value={voucherDate ? `${toYMD(voucherDate).split('-').reverse().join('/')}${paidDate ? ` / ${toYMD(paidDate).split('-').reverse().join('/')}` : ''}` : ''} />
                                <SumRow label="Account Head" value={[dcaLabel, sdcaLabel].filter(Boolean).join(' / ')} />
                                <SumRow label="Name"         value={name.trim()} />
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <SumRow label="Invoice Amount" value={`₹${fmt(num(invoiceAmount))}`} />
                                {isGST && (
                                    <SumRow
                                        label={gstType === 'IGST' ? 'IGST' : gstType === 'CGSTSGST' ? 'CGST + SGST' : 'GST'}
                                        value={`₹${fmt(gstAmount)}`}
                                    />
                                )}
                            </dl>

                            <div className={`px-4 py-3 rounded-lg text-white bg-gradient-to-r ${exceedsBalance ? 'from-amber-500 to-orange-600' : 'from-indigo-500 to-violet-600'}`}>
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">Total Amount</p>
                                <p className="text-2xl font-black">₹{fmt(totalAmount)}</p>
                                {exceedsBalance && <p className="text-[11px] text-white/90">More than the Self CC balance</p>}
                            </div>

                            {/* Message box (validation errors / save result) */}
                            {message && (
                                <div className={`rounded-lg border px-3 py-2 text-xs ${message.type === 'success'
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300'}`}>
                                    <p className="font-semibold flex items-start gap-1.5">
                                        {message.type === 'success'
                                            ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                            : <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                                        <span className="break-words">{message.title}</span>
                                    </p>
                                    {message.lines.length > 0 && (
                                        <ul className="mt-1 ml-5 list-disc space-y-0.5">
                                            {message.lines.map((l) => <li key={l}>{l}</li>)}
                                        </ul>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={busy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={busy || !voucherType}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    {busy ? 'Saving...' : 'Submit Voucher'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CashVoucherCreation;
